// Styles
import styled, { keyframes } from 'styled-components';
import colors from 'styles/colors';
import fonts from 'styles/fonts';

import PropTypes from 'prop-types';
import { useRef, useEffect, useState, createRef, useContext } from 'react';
import classnames from 'classnames';

import gsap from 'gsap';
import Draggable from 'lib/gsap/Draggable';
import InertiaPlugin from 'lib/gsap/InertiaPlugin';
import { CruiseSelectionContext } from 'contexts/CruiseSelectionContext';

import CaretBase from 'components/svg/Caret';

export const Description = styled.p`
  display: block;
  margin-top: 18px;
  font-family: ${fonts.primary.bold};
`;

const SliderContainer = styled.div``;

const Root = styled.div`
  cursor: ${props => (props.length > 1 ? 'grab' : 'auto')} !important;
`;

const Wrapper = styled.div``;

const ItemContainer = styled.div``;

export const Info = styled.div``;

const Indicators = styled.div`
  pointer-events: auto;
  left: 0;
  right: 0;
  bottom: 8px;
  display: flex;
  grid-gap: 4px;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  &:hover > span {
    transform: scale(1.3) !important;
  }
  height: 30px;
`;

const Indicator = styled.span`
  position: relative;
  display: block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${colors.secondary.deepCove};
  cursor: pointer;
  transition: transform 600ms, opacity 300ms;
  opacity: 0.5;

  &.active {
    opacity: 1;
  }
`;

const animateInNext = keyframes`
  0% {
    opacity: 0;
    transform: translateX(5px);
  } 100% {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const NextButton = styled.button`
  animation: ${animateInNext} 0.25s 0.4s cubic-bezier(0, 0, 0, 0) forwards;
`;

const animateInPrev = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-5px);
  } 100% {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const PrevButton = styled.button`
  animation: ${animateInPrev} 0.25s 0.3s cubic-bezier(0, 0, 0, 0) forwards;
`;

export const Caret = styled(CaretBase)`
  &.rotateLeft {
    transform: rotate(-180deg);
  }
`;

function makeNegative(array) {
  return array.map(x => x * -1);
}

function findClosest(array, x) {
  return array.reduce((a, b) => (Math.abs(b - x) < Math.abs(a - x) ? b : a));
}

/**
 * lorem ipsum
 * @param {string} id - Carousel id
 * @param {array} items - list of items for the Carousel
 * @param {function} showNextArrow - Determines when to show the nextArrow
 * @param {function} showPrevArrow - Determines when to show the prevArrow
 * @param {function} showIndicators - Determines when to show the indicators
 * @param {object} carouselStyles - Object with styles for each styled component
 * @param {object} carouselClasses - Object with classes for each styled component
 */

const Carousel = ({
  id,
  items,
  showNextArrow,
  showPrevArrow,
  showIndicators,
  carouselStyles,
  carouselClasses,
}) => {
  const indicatorRefs = useRef([]);
  const rootRef = useRef();
  const slides = useRef(items.map(createRef));
  const [active, setActive] = useState(0);
  const [slidesOrigins, setSlidesOrigins] = useState([]);
  const [isScrollable, setIsScrollable] = useState(false);
  const { length } = Object.keys(items);
  const { isMobile, isTablet, isTabletLandscape } = useContext(
    CruiseSelectionContext,
  );
  const isDesktop = !isTabletLandscape && !isTablet && !isMobile;

  const slidesWidth = slides.current.map(item => item.clientWidth);

  const dateId = useRef(Date.now());

  const handleResize = () => {
    let sum = 0;
    const origins = slides.current.map(function (item) {
      sum += item.getBoundingClientRect().width;
      return sum;
    });
    origins.unshift(0);
    setSlidesOrigins(origins);

    rootRef.current &&
    rootRef.current?.scrollWidth > rootRef.current?.clientWidth
      ? setIsScrollable(true)
      : setIsScrollable(false);
  };

  useEffect(() => {
    gsap.registerPlugin(Draggable);
    gsap.registerPlugin(InertiaPlugin);
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const rootBounds = rootRef.current.getBoundingClientRect().width;
    const activeBounds = rootRef.current.getBoundingClientRect().width;
    const snapPoints = makeNegative(slidesOrigins);

    gsap.to(rootRef.current, {
      x: rootBounds / 2 - activeBounds / 2 - slidesOrigins[active],
      ease: 'easeIn',
      duration: 0.5,
    });

    const animation = gsap.to(slides.current, {
      autoAlpha: 1,
      ease: 'easeIn',
      duration: 0.5,
      yoyo: true,
    });
    gsap.to(slides.current[active], {
      autoAlpha: 1,
      ease: 'easeIn',
      duration: 0.5,
    });

    function scrolling() {
      const getActive = slidesOrigins.indexOf(
        findClosest(slidesOrigins, -1 * gsap.getProperty(rootRef.current, 'x')),
      );
      setActive(Math.max(0, getActive));
      animation.progress(
        gsap.utils.snap(-1 * gsap.getProperty(rootRef.current, 'x')),
      );
    }

    Draggable.create(rootRef.current, {
      type: 'x',
      inertia: true,
      snap: { x: snapPoints },
      bounds: {
        maxX: 0,
        minX: -activeBounds * (length - 1),
      },
      onDrag: scrolling,
      onDragEnd: scrolling,
      onThrowUpdate: scrolling,
    });
  }, [active, slidesOrigins, slides, length]);

  const getKey = (prefix, idx, index) => `${prefix}-${idx}-${index}`;

  return (
    <SliderContainer id={id} css={carouselStyles.sliderContainer || ''}>
      <Root
        length={items.length}
        ref={rootRef}
        css={carouselStyles.root || ''}
        className={carouselClasses.root || ''}
      >
        <Wrapper css={carouselStyles.wrapper || ''}>
          {items.map((item, i) => {
            return (
              <ItemContainer
                css={carouselStyles.itemContainer || ''}
                key={getKey(dateId.current, id, i)}
                ref={el => (slides.current[i] = el)}
              >
                {item}
              </ItemContainer>
            );
          })}
        </Wrapper>
      </Root>
      <Info css={carouselStyles.info || ''}>
        {isScrollable && (
          <>
            {showIndicators({ isDesktop }) && (
              <Indicators id="carousel-indicators">
                {items.map((img, i) => (
                  <Indicator
                    ref={el => (indicatorRefs.current[i] = el)}
                    key={`${dateId.current}-${img.path}-${i}`}
                    onClick={() => setActive(i)}
                    className={classnames({ active: active === i })}
                  />
                ))}
              </Indicators>
            )}

            {showNextArrow({
              isDesktop,
              active,
              length,
              slidesWidth,
            }) && (
              <NextButton
                css={carouselStyles.nextButton || ''}
                className={carouselClasses.nextButton || ''}
                onClick={() => setActive(Math.min(length - 1, active + 1))}
              >
                <Caret viewAll />
              </NextButton>
            )}

            {showPrevArrow({ isDesktop, active }) && (
              <PrevButton
                css={carouselStyles.prevButton || ''}
                className={carouselClasses.prevButton || ''}
                onClick={() => setActive(Math.max(0, active - 1))}
              >
                <Caret viewAll className={classnames({ rotateLeft: true })} />
              </PrevButton>
            )}
          </>
        )}
      </Info>
    </SliderContainer>
  );
};

Carousel.propTypes = {
  id: PropTypes.string,
  items: PropTypes.array,
  showNextArrow: PropTypes.func,
  showPrevArrow: PropTypes.func,
  showIndicators: PropTypes.func,
  carouselStyles: {
    sliderContainer: PropTypes.string,
    root: PropTypes.string,
    wrapper: PropTypes.string,
    info: PropTypes.string,
    nextButton: PropTypes.string,
    prevButton: PropTypes.string,
  },
  carouselClasses: {
    sliderContainer: PropTypes.string,
    root: PropTypes.string,
    wrapper: PropTypes.string,
    info: PropTypes.string,
    nextButton: PropTypes.string,
    prevButton: PropTypes.string,
  },
};

export default Carousel;
